import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

import { AppLayout } from "@/components/layout/AppLayout";
import { RouteFallback } from "@/components/layout/RouteFallback";
import { LandingLayout } from "@/components/landing/LandingLayout";
import { Landing } from "@/pages/Landing";
import { ConsentRefused } from "@/pages/ConsentRefused";
import { PrivacyPolicy } from "@/pages/PrivacyPolicy";
import { NotFound } from "@/pages/NotFound";

/**
 * Routing SunMatch (S7-5, GAP-3) :
 * - `/` rend la landing publique (sous `LandingLayout`, **pas** de modale
 *   consentement).
 * - `/app` rend le dashboard utilisateur (sous `AppLayout`, modale RGPD
 *   active si `consent_given_at === null`).
 * - Les autres routes applicatives (wizard, résultats, settings) restent
 *   inchangées et héritent de `AppLayout`.
 * - `/politique-confidentialite` et `/consentement-refuse` restent dans
 *   `AppLayout` mais sont déjà whitelist via `PUBLIC_PATHS` côté layout
 *   (cf. `AppLayout.tsx`).
 *
 * Code-splitting (S30 A5, SEO) : la landing `/` (chunk initial) ne doit PAS
 * embarquer Leaflet (via `MapPicker`) ni Recharts (via `components/simulation`
 * + `Admin`). On garde EAGER uniquement `LandingLayout` + `Landing` + le shell
 * `AppLayout` (léger) + les pages triviales (`ConsentRefused`, `PrivacyPolicy`,
 * `NotFound`), et on charge en `React.lazy` les pages lourdes. Les exports sont
 * nommés → on ré-emballe en `default` attendu par `React.lazy`. Frontière
 * Suspense : autour de l'`<Outlet/>` dans `AppLayout` pour les enfants ;
 * par-route pour les pages d'auth standalone.
 */
const Dashboard = lazy(() =>
  import("@/pages/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const Admin = lazy(() =>
  import("@/pages/Admin").then((m) => ({ default: m.Admin })),
);
const NewSimulation = lazy(() =>
  import("@/pages/NewSimulation").then((m) => ({ default: m.NewSimulation })),
);
const SimulationResults = lazy(() =>
  import("@/pages/SimulationResults").then((m) => ({
    default: m.SimulationResults,
  })),
);
const Settings = lazy(() =>
  import("@/pages/Settings").then((m) => ({ default: m.Settings })),
);
const Sites = lazy(() =>
  import("@/pages/Sites").then((m) => ({ default: m.Sites })),
);
const Suggest = lazy(() =>
  import("@/pages/Suggest").then((m) => ({ default: m.Suggest })),
);
const Login = lazy(() =>
  import("@/pages/Login").then((m) => ({ default: m.Login })),
);
const Signup = lazy(() =>
  import("@/pages/Signup").then((m) => ({ default: m.Signup })),
);
const ForgotPassword = lazy(() =>
  import("@/pages/ForgotPassword").then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() =>
  import("@/pages/ResetPassword").then((m) => ({ default: m.ResetPassword })),
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingLayout />,
    errorElement: <NotFound />,
    children: [{ index: true, element: <Landing /> }],
  },
  // S12-7 (E1 lot 1) — pages d'authentification minimales. Standalone (pas de
  // `Navbar` ni `ConsentModal` : ce sont des pages publiques pleines page ;
  // le consentement RGPD compte est recueilli dans le formulaire signup).
  // Lazy (S30 A5) → chacune porte sa propre frontière Suspense.
  {
    path: "/signup",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Signup />
      </Suspense>
    ),
    errorElement: <NotFound />,
  },
  {
    path: "/login",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Login />
      </Suspense>
    ),
    errorElement: <NotFound />,
  },
  // S35-4 (F2) — réinitialisation du mot de passe. Standalone comme `/login` et
  // `/signup` : ce sont des pages publiques pleines page, atteintes justement
  // quand on n'a plus accès à son compte.
  {
    path: "/mot-de-passe-oublie",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <ForgotPassword />
      </Suspense>
    ),
    errorElement: <NotFound />,
  },
  // ⚠️ `/reinitialiser` — **PAS** `/reset-password`. ADR-017 D7 (*Accepted*)
  // écrit littéralement `https://sunmatch.fr/reinitialiser#token=…` et le
  // backend **envoie déjà ce lien** (`build_reset_link`) : renommer cette route
  // 404erait des liens déjà partis par e-mail. Le jeton est dans le **fragment**
  // (C6) : il n'apparaît donc pas dans le `path`, et React Router ne le voit
  // jamais (cf. `ResetPassword.tsx` / `lib/resetToken.ts`).
  {
    path: "/reinitialiser",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <ResetPassword />
      </Suspense>
    ),
    errorElement: <NotFound />,
  },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <NotFound />,
    children: [
      { path: "app", element: <Dashboard /> },
      // S21 — page admin de surveillance. Le gating réel est API-first
      // (endpoints `/admin/stats/*` gardés par `get_admin_account`) ; la page
      // affiche un 403 pour un compte non-admin (cf. `Admin.tsx`).
      { path: "admin", element: <Admin /> },
      { path: "simulations/new", element: <NewSimulation /> },
      { path: "simulations/:id", element: <SimulationResults /> },
      { path: "settings", element: <Settings /> },
      { path: "sites", element: <Sites /> },
      // S34-5 (épopée E) — boîte à idées. Accessible connecté OU anonyme, sans
      // gate de consentement (ADR-016 D4) : `/suggerer` est whitelist dans
      // `PUBLIC_PATHS` (AppLayout) pour que la modale RGPD ne l'obstrue pas.
      { path: "suggerer", element: <Suggest /> },
      // S6-5 (GAP-2) — page d'information accessible sans consentement.
      { path: "consentement-refuse", element: <ConsentRefused /> },
      // S6-6 (GAP-2) — politique de confidentialité publique RGPD art. 13.
      { path: "politique-confidentialite", element: <PrivacyPolicy /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
